'use client'

import { useContext, useEffect, useState, useCallback } from 'react'
import { AuthContext } from '@/contexts/AuthContext'
import { useRouter, useSearchParams } from 'next/navigation'
import { useGoogleLogin } from '@react-oauth/google'
import { getLocalStorageAuth } from '@/lib/auth/auth'
import LoginForm from '@/components/auth/LoginForm'

const getNextURL = (user, from) => {
  // Only honor same-origin relative paths. A bare `from` was returned verbatim, so a crafted
  // login link (?from=https://evil.com or //evil.com) would redirect the user off-site after auth.
  if (from && /^\/(?!\/)/.test(from)) {
    return from
  }
  return '/manager/dashboard'
}

export default function Home() {
  const [errors, setErrors] = useState(null)
  const [error, setError] = useState(null)
  const [errorCodes, setErrorCodes] = useState(null)
  const [loading, setLoading] = useState(false)
  const [checking, setChecking] = useState(false)
  const [isChecked, setIsChecked] = useState(false)
  const [ssoEnabled, setSsoEnabled] = useState(null) // null = loading, true/false = resolved
  const authContext = useContext(AuthContext)
  const router = useRouter()
  const searchParams = useSearchParams()
  const from = searchParams.get('from')
  const ssoToken = searchParams.get('ssoToken')
  const ssoError = searchParams.get('ssoError')

  // Handle SSO callback token
  useEffect(() => {
    if (ssoToken) {
      setLoading(true)
      // Fetch user data with the SSO token
      const headers = new Headers()
      headers.append('Authorization', 'Bearer ' + ssoToken)
      headers.append('Content-Type', 'application/json')

      fetch(
        process.env.NEXT_PUBLIC_API_URL + '/api/admin/user/is-authenticated',
        { method: 'GET', headers },
      )
        .then(res => res.json())
        .then(data => {
          if (data?.isAuthenticated && data?.user) {
            const userData = { ...data.user, token: ssoToken }
            authContext.setuser(userData, () => {
              setLoading(false)
              router.push(getNextURL(data.user, from))
            })
          } else {
            setLoading(false)
            setError('SSO authentication failed. Please try again.')
          }
        })
        .catch(() => {
          setLoading(false)
          setError('SSO authentication failed. Please try again.')
        })
    }
  }, [ssoToken])

  // Handle SSO error
  useEffect(() => {
    if (ssoError) {
      setError(decodeURIComponent(ssoError))
    }
  }, [ssoError])

  // Check SSO status
  useEffect(() => {
    fetch(process.env.NEXT_PUBLIC_API_URL + '/api/auth/saml/status')
      .then(res => res.json())
      .then(data => setSsoEnabled(data.enabled))
      .catch(() => setSsoEnabled(false))
  }, [])

  const handleSubmit = (username, password) => {
    setLoading(true)

    authContext.signin(username, password, res => {
      setLoading(false)
      if (res.body.message && res.body.errorCode) {
        setError(res.body.message)
        setErrorCodes([res.body.errorCode])
        return
      }
      if (res.body.messages) {
        setErrors(res.body.messages)
        setErrorCodes(res.body.errorCodes)
        return
      }

      setErrors(null)
      setError(null)
      router.push(getNextURL(res.body.user, from))
    })
  }

  const checkAuth = useCallback(async () => {
    setChecking(true)
    const user = getLocalStorageAuth()
    if (!user) {
      setChecking(false)
      return
    }

    const options = { method: 'GET' }
    const headers = new Headers()
    headers.append('Authorization', 'Bearer ' + user.token)
    headers.append('Content-Type', 'application/json')
    options.headers = headers
    const res = await fetch(
      process.env.NEXT_PUBLIC_API_URL + '/api/admin/user/is-authenticated',
      options,
    )

    setChecking(false)

    if (res.status === 401) return

    if (res.status !== 200) {
      setError('Unable to verify your authentication status')
      return
    }

    const data = await res.json()
    if (data?.isAuthenticated) {
      router.push('/manager/dashboard')
    }
  }, [router])

  const googleLogin = useGoogleLogin({
    onSuccess: async codeResponse => {
      setLoading(true)
      setError(null)
      setErrors(null)
      setErrorCodes(null)

      try {
        const res = await fetch(
          process.env.NEXT_PUBLIC_API_URL + '/api/user/google-login',
          {
            method: 'POST',
            body: JSON.stringify({ accessToken: codeResponse.access_token }),
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        const body = await res.json()

        if (body.success) {
          authContext.setuser({ ...authContext.user, ...body.user }, () => {
            setLoading(false)
            router.push(getNextURL(body.user, from))
          })
        } else {
          setLoading(false)
          if (body.message && body.errorCode) {
            setError(body.message)
            setErrorCodes([body.errorCode])
            return
          }
          if (body.messages) {
            setErrors(body.messages)
            setErrorCodes(body.errorCodes)
            return
          }
        }
      } catch (error) {
        setLoading(false)
        setError('An error occurred during Google login')
        console.error('Google login error:', error)
      }
    },
    onError: () => {
      setError('Login with Google failed')
    },
  })

  useEffect(() => {
    if (!isChecked) {
      checkAuth()
      setIsChecked(true)
    }
  }, [checkAuth, isChecked])

  const handleSSOLogin = () => {
    window.location.href =
      process.env.NEXT_PUBLIC_API_URL + '/api/auth/saml/login'
  }

  return (
    <LoginForm
      title="Welcome to Tectra"
      subtitle="A foundational shift in how work gets done"
      ssoEnabled={ssoEnabled}
      loading={loading}
      error={error}
      errors={errors}
      errorCodes={errorCodes}
      onSubmit={handleSubmit}
      onSSOLogin={handleSSOLogin}
      onGoogleLogin={ssoEnabled ? null : () => googleLogin()}
      showFooterLinks={!ssoEnabled}
      showEmailFallback={false}
      showProgress={checking}
      warningMessage={
        from ? `You must log in to view the page at ${from}` : null
      }
    />
  )
}
